import { lazy, Suspense } from 'react';
import { Navigate, type RouteObject } from 'react-router-dom';
import { inventoryRoutes } from '@/features/inventory/routes';
import { useAuthStore } from '@/stores/auth.store';
import { usePortalStore } from '@/stores/portal.store';
import { getScopedProfile } from '@/lib/scopedAccess';
import { AppLayout } from '@/components/layout/AppLayout';
import { PortalLayout } from '@/components/layout/PortalLayout';
import { ProtectedRoute } from './ProtectedRoute';
import { PortalProtectedRoute } from './PortalProtectedRoute';
import { FullPageSpinner } from '@/components/shared/LoadingSpinner';
import { PageSkeleton } from '@/components/shared/PageSkeleton';

// ─── Admin Pages ─────────────────────────────────────────────────────────────
const LoginPage = lazy(() => import('@/pages/auth/LoginPage'));
const DashboardPage = lazy(() => import('@/pages/dashboard/DashboardPage'));
const SubscriberListPage = lazy(() => import('@/pages/subscribers/SubscriberListPage'));
const AddSubscriberPage = lazy(() => import('@/pages/subscribers/AddSubscriberPage'));
const SubscriberDashboardPage = lazy(() => import('@/pages/subscribers/SubscriberDashboardPage'));
const SubscriberDetailPage = lazy(() => import('@/pages/subscribers/SubscriberDetailPage'));
const OnboardingListPage = lazy(() => import('@/pages/subscriber-onboarding/OnboardingListPage'));
const NewOnboardingPage = lazy(() => import('@/pages/subscriber-onboarding/NewOnboardingPage'));
const InstallationQueuePage = lazy(() => import('@/pages/subscriber-onboarding/InstallationQueuePage'));
const StaticIpRequestsPage = lazy(() => import('@/pages/subscribers/StaticIpRequestsPage'));
const ApiPartnersListPage = lazy(() => import('@/pages/api-management/ApiPartnersListPage'));
const EditPartnerPage = lazy(() => import('@/pages/api-management/EditPartnerPage'));
const ApiCredentialsPage = lazy(() => import('@/pages/api-management/ApiCredentialsPage'));
const ServiceMappingPage = lazy(() => import('@/pages/api-management/ServiceMappingPage'));
const ApiAuditLogsPage = lazy(() => import('@/pages/api-management/ApiAuditLogsPage'));
const ApiLogsPage = lazy(() => import('@/pages/api-management/ApiLogsPage'));
const ApiHealthDashboardPage = lazy(() => import('@/pages/api-management/ApiHealthDashboardPage'));
const WebhookSettingsPage = lazy(() => import('@/pages/api-management/WebhookSettingsPage'));
const IpdrDashboardPage = lazy(() => import('@/pages/ipdr/IpdrDashboardPage'));
const IpdrSearchPage = lazy(() => import('@/pages/ipdr/IpdrSearchPage'));
const IpdrCasesPage = lazy(() => import('@/pages/ipdr/IpdrCasesPage'));
const IpdrCaseDetailPage = lazy(() => import('@/pages/ipdr/IpdrCaseDetailPage'));
const IpdrIngestionPage = lazy(() => import('@/pages/ipdr/IpdrIngestionPage'));
const IpdrAuditPage = lazy(() => import('@/pages/ipdr/IpdrAuditPage'));
const IpdrServerDashboardPage = lazy(() => import('@/pages/ipdr/IpdrServerDashboardPage'));
const IpdrStorageAnalyticsPage = lazy(() => import('@/pages/ipdr/IpdrStorageAnalyticsPage'));
const IpdrRouterDetailPage = lazy(() => import('@/pages/ipdr/IpdrRouterDetailPage'));
const CommandCenterPage = lazy(() => import('@/pages/dispatch/CommandCenterPage'));
const ReportCenterPage = lazy(() => import('@/pages/dispatch/ReportCenterPage'));
const FieldSettingsPage = lazy(() => import('@/pages/dispatch/FieldSettingsPage'));
const DispatchBoardPage = lazy(() => import('@/pages/dispatch/DispatchBoardPage'));
const FieldDispatchPage = lazy(() => import('@/pages/dispatch/FieldDispatchPage'));
const FieldWorkforcePage = lazy(() => import('@/pages/dispatch/FieldWorkforcePage'));
const FieldInsightsPage = lazy(() => import('@/pages/dispatch/FieldInsightsPage'));
const DispatchMapPage = lazy(() => import('@/pages/dispatch/DispatchMapPage'));
const TechnicianHistoryPage = lazy(() => import('@/pages/dispatch/TechnicianHistoryPage'));
const EmployeeMonitoringPage = lazy(() => import('@/pages/dispatch/EmployeeMonitoringPage'));
const TechnicianPerformancePage = lazy(() => import('@/pages/dispatch/TechnicianPerformancePage'));
const TechnicianProfile360Page = lazy(() => import('@/pages/dispatch/TechnicianProfile360Page'));
const OutageConsolePage = lazy(() => import('@/pages/dispatch/OutageConsolePage'));
const ExpenseApprovalsPage = lazy(() => import('@/pages/dispatch/ExpenseApprovalsPage'));
const GeofencesPage = lazy(() => import('@/pages/dispatch/GeofencesPage'));
const EkycSubmitPage = lazy(() => import('@/pages/public/EkycSubmitPage'));
const CafSigningPage = lazy(() => import('@/pages/public/CafSigningPage'));
const RateJobPage = lazy(() => import('@/pages/public/RateJobPage'));
const PayInvoicePage = lazy(() => import('@/pages/public/PayInvoicePage'));
const ExpiredPage = lazy(() => import('@/pages/public/ExpiredPage'));
const AutopayPage = lazy(() => import('@/pages/billing/AutopayPage'));
const AutopayEnrolPage = lazy(() => import('@/pages/public/AutopayEnrolPage'));
const ReminderSettingsPage = lazy(() => import('@/pages/billing/ReminderSettingsPage'));
const ReminderDryRunPage = lazy(() => import('@/pages/billing/ReminderDryRunPage'));
const BillingPage = lazy(() => import('@/pages/billing/BillingPage'));
const InvoiceDetailPage = lazy(() => import('@/pages/billing/InvoiceDetailPage'));
const BillingAutomationPage = lazy(() => import('@/pages/billing/BillingAutomationPage'));
const CollectionsPage = lazy(() => import('@/pages/billing/CollectionsPage'));
const PaymentGatewayPage = lazy(() => import('@/pages/billing/PaymentGatewayPage'));
const InvoiceTemplatesPage = lazy(() => import('@/pages/billing/InvoiceTemplatesPage'));
const ReconciliationPage = lazy(() => import('@/pages/billing/ReconciliationPage'));
const TaxSettingsPage = lazy(() => import('@/pages/billing/TaxSettingsPage'));
const PlansPage = lazy(() => import('@/pages/plans/PlansPage'));
const PlanFormPage = lazy(() => import('@/pages/plans/PlanFormPage'));
const TicketsPage = lazy(() => import('@/pages/tickets/TicketsPage'));
const NocPage = lazy(() => import('@/pages/noc/NocPage'));
const AlertRulesPage = lazy(() => import('@/pages/noc/AlertRulesPage'));
const AlertHistoryPage = lazy(() => import('@/pages/noc/AlertHistoryPage'));
const OltModulePage = lazy(() => import('@/pages/olt/OltModulePage'));
const OltOverviewPage = lazy(() => import('@/pages/olt/OltOverviewPage'));
const OltAnalyticsPage = lazy(() => import('@/pages/olt/OltAnalyticsPage'));
const WifiReadinessPage = lazy(() => import('@/pages/olt/WifiReadinessPage'));
const OltDetailPage = lazy(() => import('@/pages/olt/OltDetailPage'));
const OnuDetailPage = lazy(() => import('@/pages/olt/OnuDetailPage'));
const OnuMovementHistoryPage = lazy(() => import('@/pages/olt/OnuMovementHistoryPage'));
const DuplicateOnuPage = lazy(() => import('@/pages/olt/DuplicateOnuPage'));
const NasFailoverPage = lazy(() => import('@/pages/nas/FailoverPage'));
const NasManagementPage = lazy(() => import('@/pages/network/nas/NasManagementPage'));
const NasFormPage = lazy(() => import('@/pages/network/nas/NasFormPage'));
const NasDetailPage = lazy(() => import('@/pages/network/nas/NasDetailPage'));
const NasSessionsPage = lazy(() => import('@/pages/network/nas/NasSessionsPage'));
const ReportsPage = lazy(() => import('@/pages/reports/ReportsPage'));
const ReportBuilderPage = lazy(() => import('@/pages/reports/ReportBuilderPage'));
const BusinessIntelligencePage = lazy(() => import('@/pages/reports/BusinessIntelligencePage'));
const SettingsPage = lazy(() => import('@/pages/settings/SettingsPage'));
const SettingsHubPage = lazy(() => import('@/pages/settings/SettingsHubPage'));
const GstSettingsPage = lazy(() => import('@/pages/settings/GstSettingsPage'));
const LogsPage = lazy(() => import('@/pages/logs/LogsPage'));
const LiveLogsPage = lazy(() => import('@/pages/logs/LiveLogsPage'));
const IpamPage = lazy(() => import('@/pages/ipam/IpamPage'));
const NotificationsPage = lazy(() => import('@/pages/notifications/NotificationsPage'));
const JobsDashboardPage = lazy(() => import('@/pages/jobs/JobsDashboardPage'));
const AnalyticsPage = lazy(() => import('@/pages/analytics/AnalyticsPage'));
const AdvancedAnalyticsPage = lazy(() => import('@/pages/analytics/AdvancedAnalyticsPage'));
const BandwidthPage = lazy(() => import('@/pages/bandwidth/BandwidthPage'));
const StaffPage = lazy(() => import('@/pages/settings/StaffPage'));
const TemplatePage = lazy(() => import('@/pages/notifications/TemplatePage'));
const LeadDashboardPage = lazy(() => import('@/pages/leads/LeadDashboardPage'));
const AllLeadsPage = lazy(() => import('@/pages/leads/AllLeadsPage'));
const AddLeadPage = lazy(() => import('@/pages/leads/AddLeadPage'));
const ModuleLicensingPage = lazy(() => import('@/pages/settings/ModuleLicensingPage'));
const OnuWifiModelsPage = lazy(() => import('@/pages/settings/OnuWifiModelsPage'));
const VoiceSipProfilesPage = lazy(() => import('@/pages/settings/VoiceSipProfilesPage'));
const FirmwareLibraryPage = lazy(() => import('@/pages/settings/FirmwareLibraryPage'));
const BulkSendPage = lazy(() => import('@/pages/notifications/BulkSendPage'));
const EmailTemplatesPage = lazy(() => import('@/pages/notifications/EmailTemplatesPage'));
const DataIoPage = lazy(() => import('@/pages/data-io/DataIoPage'));
const SystemHealthPage = lazy(() => import('@/pages/system/SystemHealthPage'));
const RolesPage = lazy(() => import('@/pages/roles/RolesPage'));
const PreferencesPage = lazy(() => import('@/pages/settings/PreferencesPage'));
const WebhooksPage = lazy(() => import('@/pages/webhooks/WebhooksPage'));
const ScheduledReportsPage = lazy(() => import('@/pages/scheduled-reports/ScheduledReportsPage'));
const SlaPage = lazy(() => import('@/pages/sla/SlaPage'));
const KycCallbackPage = lazy(() => import('@/pages/kyc/KycCallbackPage'));
const ApiKeysPage = lazy(() => import('@/pages/api-keys/ApiKeysPage'));
const ApiUsagePage = lazy(() => import('@/pages/api-keys/ApiUsagePage'));
const MaintenancePage = lazy(() => import('@/pages/maintenance/MaintenancePage'));
const ExpensesPage = lazy(() => import('@/pages/expenses/ExpensesPage'));
const TopologyPage = lazy(() => import('@/pages/network/TopologyPage'));
const BackupPage = lazy(() => import('@/pages/settings/BackupPage'));
const SsoSettingsPage = lazy(() => import('@/pages/settings/SsoSettingsPage'));
const EkycSettingsPage = lazy(() => import('@/pages/settings/EkycSettingsPage'));
const DocumentRequirementsPage = lazy(() => import('@/pages/settings/DocumentRequirementsPage'));
const PartnerCommissionDefaultsPage = lazy(() => import('@/pages/partners/CommissionDefaultsPage'));
const CafTemplateSettingsPage = lazy(() => import('@/pages/settings/CafTemplateSettingsPage').then(m => ({ default: m.CafTemplateSettingsPage })));
const InstallationSettingsPage = lazy(() => import('@/pages/settings/InstallationSettingsPage').then(m => ({ default: m.InstallationSettingsPage })));
const TwoFactorPage = lazy(() => import('@/pages/settings/TwoFactorPage'));
const WhatsAppSettingsPage = lazy(() => import('@/pages/settings/WhatsAppSettingsPage'));
const WhatsAppAnalyticsPage = lazy(() => import('@/pages/settings/WhatsAppAnalyticsPage'));
const WhatsAppTemplatesPage = lazy(() => import('@/pages/settings/WhatsAppTemplatesPage'));
const WhatsAppInboxPage = lazy(() => import('@/pages/whatsapp/WhatsAppInboxPage'));
const WhatsAppAutomationPage = lazy(() => import('@/pages/settings/WhatsAppAutomationPage'));
const FirmwarePage = lazy(() => import('@/pages/firmware/FirmwarePage'));
const OnboardingPage = lazy(() => import('@/pages/onboarding/OnboardingPage'));
const AuditTrailPage = lazy(() => import('@/pages/audit/AuditTrailPage'));
const CampaignsPage = lazy(() => import('@/pages/campaigns/CampaignsPage'));
const RadiusManagerPage = lazy(() => import('@/pages/radius/RadiusPage'));
const PppoeManagementPage = lazy(() => import('@/pages/radius/PppoeManagementPage'));
const NetworkManagementPage = lazy(() => import('@/pages/network/NetworkManagementPage'));
const CompliancePage = lazy(() => import('@/pages/compliance/CompliancePage'));
const PartnersPage = lazy(() => import('@/pages/partners/PartnersPage'));
const PartnerListPage = lazy(() => import('@/pages/partners/PartnerListPage'));
const PartnerDetailPage = lazy(() => import('@/pages/partners/PartnerDetailPage'));
const PartnerPortalPage = lazy(() => import('@/pages/partners/PartnerPortalPage'));
const PartnerDashboardPage = lazy(() => import('@/pages/partners/PartnerDashboardPage'));
const PartnerCommissionReportPage = lazy(() => import('@/pages/partners/CommissionReportPage'));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));

// ─── Customer Portal Pages ───────────────────────────────────────────────────
const PortalLoginPage = lazy(() => import('@/pages/portal/PortalLoginPage'));
const PortalDashboardPage = lazy(() => import('@/pages/portal/PortalDashboardPage'));
const PortalInvoicesPage = lazy(() => import('@/pages/portal/PortalInvoicesPage'));
const PortalPaymentsPage = lazy(() => import('@/pages/portal/PortalPaymentsPage'));
const PortalTicketsPage = lazy(() => import('@/pages/portal/PortalTicketsPage'));
const PortalProfilePage = lazy(() => import('@/pages/portal/PortalProfilePage'));
const PortalUsagePage = lazy(() => import('@/pages/portal/PortalUsagePage'));
const PortalConnectionPage = lazy(() => import('@/pages/portal/PortalConnectionPage'));
const PlanUpgradePage = lazy(() => import('@/pages/portal/PlanUpgradePage'));

/** Full-screen spinner for login & 404 pages (no layout shell) */
function SuspenseWrapper({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<FullPageSpinner />}>{children}</Suspense>;
}

/** Lightweight page skeleton for authenticated routes (layout shell visible) */
function PageSuspense({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<PageSkeleton />}>{children}</Suspense>;
}

function Protected({ children }: { children: React.ReactNode }) {
  return (
    <ProtectedRoute>
      <SuspenseWrapper>{children}</SuspenseWrapper>
    </ProtectedRoute>
  );
}

/** Root '/' redirect: scoped roles (e.g. OLT operator) go to their home, everyone else to /dashboard. */
/**
 * Where "/" sends you, which depends entirely on who you are.
 *
 * This is the address printed on the "Open Customer Portal" button in the WhatsApp welcome message,
 * and that template is approved at Meta and cannot be edited — so "/" has to be right for a
 * customer, not just for staff. It used to send everyone to the staff dashboard, which bounced
 * signed-out visitors to the staff sign-in page; a customer has no staff account, so the button
 * looked broken.
 *
 * Signed-in sessions keep their own landing page. Only an anonymous visitor is treated as a
 * customer, which is the right guess: staff reach the admin login directly and have it bookmarked.
 */
function RootRedirect() {
  const user = useAuthStore((s) => s.user);
  const staffAuthed = useAuthStore((s) => s.isAuthenticated);
  const portalAuthed = usePortalStore((s) => s.isAuthenticated);

  if (staffAuthed) return <Navigate to={getScopedProfile(user?.permissions, user?.partnerId)?.home ?? '/dashboard'} replace />;
  if (portalAuthed) return <Navigate to="/portal" replace />;
  return <Navigate to="/portal/login" replace />;
}

function PortalProtected({ children }: { children: React.ReactNode }) {
  return (
    <PortalProtectedRoute>
      <SuspenseWrapper>{children}</SuspenseWrapper>
    </PortalProtectedRoute>
  );
}

export const routes: RouteObject[] = [
  {
    path: '/',
    element: <RootRedirect />,
  },
  {
    path: '/login',
    element: (
      <SuspenseWrapper>
        <LoginPage />
      </SuspenseWrapper>
    ),
  },

  // ─── Public customer eKYC self-service (no auth, no sidebar) ──────
  {
    path: '/ekyc/submit/:token',
    element: (
      <SuspenseWrapper>
        <EkycSubmitPage />
      </SuspenseWrapper>
    ),
  },
  {
    // Public by design: the customer signing a CAF has no portal login, so the single-use token
    // in the URL is the authorisation.
    path: '/caf/sign/:token',
    element: (
      <SuspenseWrapper>
        <CafSigningPage />
      </SuspenseWrapper>
    ),
  },
  {
    path: '/rate/:token',
    element: (
      <SuspenseWrapper>
        <RateJobPage />
      </SuspenseWrapper>
    ),
  },
  // Where the NAS sends an expired customer's traffic. No auth: they have no working connection to
  // sign in with, and the page identifies them from the source IP of their own walled-garden session.
  {
    path: '/expired',
    element: (
      <SuspenseWrapper>
        <ExpiredPage />
      </SuspenseWrapper>
    ),
  },
  // Customer payment link from a WhatsApp reminder. No auth, no sidebar — the token is the credential.
  {
    path: '/pay/:token',
    element: (
      <SuspenseWrapper>
        <PayInvoicePage />
      </SuspenseWrapper>
    ),
  },
  // Customer AutoPay consent screen, reached from a WhatsApp link. No auth.
  {
    path: '/autopay/:token',
    element: (
      <SuspenseWrapper>
        <AutopayEnrolPage />
      </SuspenseWrapper>
    ),
  },

  // ─── Onboarding Wizard (full-screen, no sidebar) ─────────────────
  {
    path: '/onboarding',
    element: (
      <Protected>
        <OnboardingPage />
      </Protected>
    ),
  },

  // ─── Admin Routes ────────────────────────────────────────────────
  {
    element: (
      <Protected>
        <AppLayout />
      </Protected>
    ),
    children: [
      { path: '/dashboard', element: <PageSuspense><DashboardPage /></PageSuspense> },
      { path: '/leads', element: <PageSuspense><AllLeadsPage /></PageSuspense> },
      { path: '/leads/dashboard', element: <PageSuspense><LeadDashboardPage /></PageSuspense> },
      { path: '/leads/new', element: <PageSuspense><AddLeadPage /></PageSuspense> },
      { path: '/subscribers', element: <PageSuspense><SubscriberListPage /></PageSuspense> },
      { path: '/subscribers/new', element: <PageSuspense><AddSubscriberPage /></PageSuspense> },
      { path: '/subscribers/dashboard', element: <PageSuspense><SubscriberDashboardPage /></PageSuspense> },
      { path: '/subscribers/:id', element: <PageSuspense><SubscriberDetailPage /></PageSuspense> },
      { path: '/subscribers/static-ip-requests', element: <PageSuspense><StaticIpRequestsPage /></PageSuspense> },
      { path: '/subscriber-onboarding', element: <PageSuspense><OnboardingListPage /></PageSuspense> },
      { path: '/subscriber-onboarding/new', element: <PageSuspense><NewOnboardingPage /></PageSuspense> },
      { path: '/subscriber-onboarding/installations', element: <PageSuspense><InstallationQueuePage /></PageSuspense> },
      { path: '/api-management/partners', element: <PageSuspense><ApiPartnersListPage /></PageSuspense> },
      { path: '/api-management/partners/new', element: <PageSuspense><EditPartnerPage /></PageSuspense> },
      { path: '/api-management/partners/:id', element: <PageSuspense><EditPartnerPage /></PageSuspense> },
      { path: '/api-management/credentials', element: <PageSuspense><ApiCredentialsPage /></PageSuspense> },
      { path: '/api-management/service-mapping', element: <PageSuspense><ServiceMappingPage /></PageSuspense> },
      { path: '/api-management/webhooks', element: <PageSuspense><WebhookSettingsPage /></PageSuspense> },
      { path: '/api-management/health', element: <PageSuspense><ApiHealthDashboardPage /></PageSuspense> },
      { path: '/api-management/logs', element: <PageSuspense><ApiLogsPage /></PageSuspense> },
      { path: '/api-management/audit', element: <PageSuspense><ApiAuditLogsPage /></PageSuspense> },
      { path: '/ipdr', element: <PageSuspense><IpdrDashboardPage /></PageSuspense> },
      { path: '/ipdr/server', element: <PageSuspense><IpdrServerDashboardPage /></PageSuspense> },
      { path: '/ipdr/storage-analytics', element: <PageSuspense><IpdrStorageAnalyticsPage /></PageSuspense> },
      { path: '/ipdr/router/:ip', element: <PageSuspense><IpdrRouterDetailPage /></PageSuspense> },
      { path: '/ipdr/search', element: <PageSuspense><IpdrSearchPage /></PageSuspense> },
      { path: '/ipdr/cases', element: <PageSuspense><IpdrCasesPage /></PageSuspense> },
      { path: '/ipdr/cases/:id', element: <PageSuspense><IpdrCaseDetailPage /></PageSuspense> },
      { path: '/ipdr/ingestion', element: <PageSuspense><IpdrIngestionPage /></PageSuspense> },
      { path: '/ipdr/audit', element: <PageSuspense><IpdrAuditPage /></PageSuspense> },
      { path: '/dispatch/command', element: <PageSuspense><CommandCenterPage /></PageSuspense> },
      { path: '/dispatch/reports', element: <PageSuspense><ReportCenterPage /></PageSuspense> },
      { path: '/dispatch/settings', element: <PageSuspense><FieldSettingsPage /></PageSuspense> },
      // The workspace takes /dispatch; the board it replaces keeps working at /dispatch/board so no
      // bookmark, and no link already sent, breaks during the transition.
      { path: '/dispatch', element: <PageSuspense><FieldDispatchPage /></PageSuspense> },
      { path: '/dispatch/board', element: <PageSuspense><DispatchBoardPage /></PageSuspense> },
      { path: '/dispatch/map', element: <PageSuspense><DispatchMapPage /></PageSuspense> },
      { path: '/dispatch/history', element: <PageSuspense><TechnicianHistoryPage /></PageSuspense> },
      { path: '/dispatch/employees', element: <PageSuspense><EmployeeMonitoringPage /></PageSuspense> },
      { path: '/dispatch/technicians', element: <PageSuspense><TechnicianPerformancePage /></PageSuspense> },
      { path: '/dispatch/technicians/:userId', element: <PageSuspense><TechnicianProfile360Page /></PageSuspense> },
      { path: '/dispatch/outages', element: <PageSuspense><OutageConsolePage /></PageSuspense> },
      { path: '/dispatch/expenses', element: <PageSuspense><ExpenseApprovalsPage /></PageSuspense> },
      // Technician van stock is gone — technicians hold material only as custody for a job. Old links land there.
      { path: '/dispatch/inventory', element: <Navigate to="/inventory/jobs/custody" replace /> },
      { path: '/dispatch/geofences', element: <PageSuspense><GeofencesPage /></PageSuspense> },
      // Consolidated homes. The pages they absorb keep their own routes so existing links still work.
      { path: '/dispatch/workforce', element: <PageSuspense><FieldWorkforcePage /></PageSuspense> },
      // Resources held van stock and expense approvals; with van stock gone it is just the approvals.
      { path: '/dispatch/resources', element: <Navigate to="/dispatch/expenses" replace /> },
      { path: '/dispatch/insights', element: <PageSuspense><FieldInsightsPage /></PageSuspense> },
      { path: '/billing', element: <PageSuspense><BillingPage /></PageSuspense> },
      { path: '/billing/invoices/:id', element: <PageSuspense><InvoiceDetailPage /></PageSuspense> },
      { path: '/billing/automation', element: <PageSuspense><BillingAutomationPage /></PageSuspense> },
      { path: '/billing/collections', element: <PageSuspense><CollectionsPage /></PageSuspense> },
      { path: '/billing/gateway', element: <PageSuspense><PaymentGatewayPage /></PageSuspense> },
      { path: '/billing/templates', element: <PageSuspense><InvoiceTemplatesPage /></PageSuspense> },
      { path: '/billing/tax', element: <PageSuspense><TaxSettingsPage /></PageSuspense> },
      { path: '/billing/reconciliation', element: <PageSuspense><ReconciliationPage /></PageSuspense> },
      { path: '/billing/autopay', element: <PageSuspense><AutopayPage /></PageSuspense> },
      { path: '/billing/reminders', element: <PageSuspense><ReminderSettingsPage /></PageSuspense> },
      { path: '/billing/reminders/dry-run', element: <PageSuspense><ReminderDryRunPage /></PageSuspense> },
      { path: '/plans', element: <PageSuspense><PlansPage /></PageSuspense> },
      { path: '/plans/new', element: <PageSuspense><PlanFormPage /></PageSuspense> },
      { path: '/plans/:id/edit', element: <PageSuspense><PlanFormPage /></PageSuspense> },
      { path: '/tickets', element: <PageSuspense><TicketsPage /></PageSuspense> },
      { path: '/noc', element: <PageSuspense><NocPage /></PageSuspense> },
      { path: '/noc/alert-rules', element: <PageSuspense><AlertRulesPage /></PageSuspense> },
      { path: '/noc/history', element: <PageSuspense><AlertHistoryPage /></PageSuspense> },
      { path: '/bandwidth', element: <PageSuspense><BandwidthPage /></PageSuspense> },
      { path: '/pppoe', element: <Navigate to="/network/sessions" replace /> },
      { path: '/olt', element: <PageSuspense><OltModulePage /></PageSuspense> },
      { path: '/olt/overview', element: <PageSuspense><OltOverviewPage /></PageSuspense> },
      { path: '/olt/analytics', element: <PageSuspense><OltAnalyticsPage /></PageSuspense> },
      { path: '/olt/wifi-readiness', element: <PageSuspense><WifiReadinessPage /></PageSuspense> },
      // ONU Movement History. Both entries MUST stay above '/olt/:tab' below — that catch-all
      // matches any single segment and would otherwise render the OLT module shell here instead
      // of the movement log (silently: a wrong page, no error). '/olt/movement-history' is the
      // canonical path used by the sidebar; '/olt/onu-movements' is kept as an alias so links
      // and bookmarks made against the earlier path do not fall through to the catch-all.
      { path: '/olt/movement-history', element: <PageSuspense><OnuMovementHistoryPage /></PageSuspense> },
      { path: '/olt/onu-movements', element: <PageSuspense><OnuMovementHistoryPage /></PageSuspense> },
      // Duplicate ONU. Same rule as the two entries above: this MUST stay above '/olt/:tab' —
      // that catch-all matches any single segment, so below it '/olt/duplicate-onu' would bind
      // :tab='duplicate-onu' and silently render the OLT module shell instead of this page.
      { path: '/olt/duplicate-onu', element: <PageSuspense><DuplicateOnuPage /></PageSuspense> },
      { path: '/olt/device/:id', element: <PageSuspense><OltDetailPage /></PageSuspense> },
      { path: '/olt/device/:oltId/onu/:onuId', element: <PageSuspense><OnuDetailPage /></PageSuspense> },
      { path: '/olt/:tab', element: <PageSuspense><OltModulePage /></PageSuspense> },
      { path: '/nas', element: <Navigate to="/network/nas" replace /> },
      { path: '/nas/monitoring', element: <Navigate to="/network/nas" replace /> },
      { path: '/nas/failover', element: <PageSuspense><NasFailoverPage /></PageSuspense> },
      { path: '/network/nas', element: <PageSuspense><NasManagementPage /></PageSuspense> },
      { path: '/network/nas/new', element: <PageSuspense><NasFormPage /></PageSuspense> },
      { path: '/network/nas/:id', element: <PageSuspense><NasDetailPage /></PageSuspense> },
      { path: '/network/nas/:id/edit', element: <PageSuspense><NasFormPage /></PageSuspense> },
      { path: '/network/sessions', element: <PageSuspense><NasSessionsPage /></PageSuspense> },
      { path: '/network/topology', element: <PageSuspense><TopologyPage /></PageSuspense> },
      ...inventoryRoutes,
      { path: '/ipam', element: <PageSuspense><IpamPage /></PageSuspense> },
      { path: '/notifications', element: <PageSuspense><NotificationsPage /></PageSuspense> },
      { path: '/notifications/templates', element: <PageSuspense><TemplatePage /></PageSuspense> },
      { path: '/notifications/bulk', element: <PageSuspense><BulkSendPage /></PageSuspense> },
      { path: '/notifications/email-templates', element: <PageSuspense><EmailTemplatesPage /></PageSuspense> },
      { path: '/analytics', element: <PageSuspense><AnalyticsPage /></PageSuspense> },
      { path: '/analytics/advanced', element: <PageSuspense><AdvancedAnalyticsPage /></PageSuspense> },
      { path: '/reports', element: <PageSuspense><ReportsPage /></PageSuspense> },
      { path: '/reports/builder', element: <PageSuspense><ReportBuilderPage /></PageSuspense> },
      { path: '/reports/bi', element: <PageSuspense><BusinessIntelligencePage /></PageSuspense> },
      { path: '/jobs', element: <PageSuspense><JobsDashboardPage /></PageSuspense> },
      { path: '/settings/staff', element: <PageSuspense><StaffPage /></PageSuspense> },
      { path: '/settings/modules', element: <PageSuspense><ModuleLicensingPage /></PageSuspense> },
      { path: '/settings/onu-wifi-models', element: <PageSuspense><OnuWifiModelsPage /></PageSuspense> },
      { path: '/settings/voice-sip', element: <PageSuspense><VoiceSipProfilesPage /></PageSuspense> },
      { path: '/settings/firmware-library', element: <PageSuspense><FirmwareLibraryPage /></PageSuspense> },
      { path: '/settings/backup', element: <PageSuspense><BackupPage /></PageSuspense> },
      { path: '/settings/sso', element: <PageSuspense><SsoSettingsPage /></PageSuspense> },
      { path: '/settings/ekyc', element: <PageSuspense><EkycSettingsPage /></PageSuspense> },
      { path: '/settings/document-requirements', element: <PageSuspense><DocumentRequirementsPage /></PageSuspense> },
      { path: '/settings/caf-template', element: <PageSuspense><CafTemplateSettingsPage /></PageSuspense> },
      { path: '/settings/installation', element: <PageSuspense><InstallationSettingsPage /></PageSuspense> },
      { path: '/settings/partner-commission', element: <PageSuspense><PartnerCommissionDefaultsPage /></PageSuspense> },
      { path: '/settings/whatsapp', element: <PageSuspense><WhatsAppSettingsPage /></PageSuspense> },
      { path: '/settings/whatsapp/analytics', element: <PageSuspense><WhatsAppAnalyticsPage /></PageSuspense> },
      { path: '/settings/whatsapp/templates', element: <PageSuspense><WhatsAppTemplatesPage /></PageSuspense> },
      { path: '/whatsapp/inbox', element: <PageSuspense><WhatsAppInboxPage /></PageSuspense> },
      { path: '/settings/whatsapp/automation', element: <PageSuspense><WhatsAppAutomationPage /></PageSuspense> },
      { path: '/settings/2fa', element: <PageSuspense><TwoFactorPage /></PageSuspense> },
      { path: '/settings/gst', element: <PageSuspense><GstSettingsPage /></PageSuspense> },
      { path: '/settings', element: <PageSuspense><SettingsHubPage /></PageSuspense> },
      { path: '/settings/organization', element: <PageSuspense><SettingsPage /></PageSuspense> },
      // Catch-all kept so any pre-existing /settings/<x> link still resolves.
      { path: '/settings/*', element: <PageSuspense><SettingsPage /></PageSuspense> },
      { path: '/import-export', element: <PageSuspense><DataIoPage /></PageSuspense> },
      { path: '/system-health', element: <PageSuspense><SystemHealthPage /></PageSuspense> },
      { path: '/roles', element: <PageSuspense><RolesPage /></PageSuspense> },
      { path: '/preferences', element: <PageSuspense><PreferencesPage /></PageSuspense> },
      { path: '/webhooks', element: <PageSuspense><WebhooksPage /></PageSuspense> },
      { path: '/scheduled-reports', element: <PageSuspense><ScheduledReportsPage /></PageSuspense> },
      { path: '/sla', element: <PageSuspense><SlaPage /></PageSuspense> },
      { path: '/kyc/callback', element: <PageSuspense><KycCallbackPage /></PageSuspense> },
      { path: '/api-keys', element: <PageSuspense><ApiKeysPage /></PageSuspense> },
      { path: '/api-usage', element: <PageSuspense><ApiUsagePage /></PageSuspense> },
      { path: '/maintenance', element: <PageSuspense><MaintenancePage /></PageSuspense> },
      { path: '/expenses', element: <PageSuspense><ExpensesPage /></PageSuspense> },
      { path: '/firmware', element: <PageSuspense><FirmwarePage /></PageSuspense> },
      { path: '/audit', element: <PageSuspense><AuditTrailPage /></PageSuspense> },
      { path: '/logs', element: <PageSuspense><LogsPage /></PageSuspense> },
      { path: '/logs/live', element: <PageSuspense><LiveLogsPage /></PageSuspense> },
      { path: '/campaigns', element: <PageSuspense><CampaignsPage /></PageSuspense> },
      { path: '/radius-manager', element: <PageSuspense><RadiusManagerPage /></PageSuspense> },
      { path: '/radius/pppoe', element: <PageSuspense><PppoeManagementPage /></PageSuspense> },
      { path: '/network/management', element: <PageSuspense><NetworkManagementPage /></PageSuspense> },
      { path: '/compliance', element: <PageSuspense><CompliancePage /></PageSuspense> },
      { path: '/partners', element: <PageSuspense><PartnerListPage /></PageSuspense> },
      { path: '/partners/dashboard', element: <PageSuspense><PartnerDashboardPage /></PageSuspense> },
      // Operators, service areas, commissions and settlements — the page that used to be /partners.
      { path: '/partners/operations', element: <PageSuspense><PartnersPage /></PageSuspense> },
      { path: '/partners/commission-report', element: <PageSuspense><PartnerCommissionReportPage /></PageSuspense> },
      { path: '/partners/:id', element: <PageSuspense><PartnerDetailPage /></PageSuspense> },
      { path: '/partner-portal', element: <PageSuspense><PartnerPortalPage /></PageSuspense> },
    ],
  },

  // ─── Customer Portal Routes ──────────────────────────────────────
  {
    path: '/portal/login',
    element: (
      <SuspenseWrapper>
        <PortalLoginPage />
      </SuspenseWrapper>
    ),
  },
  {
    element: (
      <PortalProtected>
        <PortalLayout />
      </PortalProtected>
    ),
    children: [
      { path: '/portal', element: <PageSuspense><PortalDashboardPage /></PageSuspense> },
      { path: '/portal/invoices', element: <PageSuspense><PortalInvoicesPage /></PageSuspense> },
      { path: '/portal/payments', element: <PageSuspense><PortalPaymentsPage /></PageSuspense> },
      { path: '/portal/connection', element: <PageSuspense><PortalConnectionPage /></PageSuspense> },
      { path: '/portal/usage', element: <PageSuspense><PortalUsagePage /></PageSuspense> },
      { path: '/portal/tickets', element: <PageSuspense><PortalTicketsPage /></PageSuspense> },
      { path: '/portal/upgrade', element: <PageSuspense><PlanUpgradePage /></PageSuspense> },
      { path: '/portal/profile', element: <PageSuspense><PortalProfilePage /></PageSuspense> },
    ],
  },

  {
    path: '*',
    element: (
      <SuspenseWrapper>
        <NotFoundPage />
      </SuspenseWrapper>
    ),
  },
];
